import { NgClass } from "@angular/common";
import { AfterViewInit, Component, DoCheck, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject } from "@angular/core";
import { ControlContainer, FormsModule, NgModelGroup, ReactiveFormsModule } from "@angular/forms";
import { ClassDirective } from "@ngbracket/ngx-layout/extended";
import { KeyHandlerService, SecurityService } from "@tylertech/core-module";
import { FieldDensity } from "@tylertech/forge";
import { ForgeCircularProgressModule, ForgeTextFieldModule } from "@tylertech/forge-angular";
import { EMPTY_GUID, GuidHelper, Utilities } from "@tylertech/shared-module";
import { PropertyMetadata } from "@tylertech/tyler-api-types";
import { Subject } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";
import { FocusOnLoadDirective } from "../../../../directives/focusOnLoad.directive";
import { AugmentedModelObject, ValueChangedParms } from "../../../../interfaces";
import { MetadataInputFormatterService, MetadataInputTypeHelperService, MetadataPropertyService, MetadataService } from "../../../../services";

@Component({
    selector: "metadata-input-phone-number",
    templateUrl: "./metadata-input-phone-number.tmpl.html",
    viewProviders: [{ provide: ControlContainer, useExisting: NgModelGroup }],
    imports: [
        ForgeTextFieldModule,
        NgClass,
        ClassDirective,
        ReactiveFormsModule,
        FocusOnLoadDirective,
        FormsModule,
        ForgeCircularProgressModule,
    ],
})
// @LogComponentChanges
export class MetadataInputPhoneNumberComponent implements OnInit, AfterViewInit, OnChanges, DoCheck, OnDestroy
{
    modelGroup = inject(NgModelGroup);
    private $metadataService = inject(MetadataService);
    private readonly $metadataPropertyService = inject(MetadataPropertyService);
    private readonly $metadataInputFormatterService = inject(MetadataInputFormatterService);
    private readonly $metadataInputTypeHelperService = inject(MetadataInputTypeHelperService);
    private readonly $keyHandlerService = inject(KeyHandlerService);
    private readonly $securityService = inject(SecurityService);

    @Input()
    model: string;

    @Input()
    metadataModel: AugmentedModelObject; // model should be a field on metadataModel, FYI

    @Input()
    parent: AugmentedModelObject; // metadataModel should be a field on parent, FYI

    @Input()
    viewOnly?: boolean;

    @Input()
    propertyMetadata: PropertyMetadata;

    @Input()
    dense?: boolean;

    @Input()
    forCellEdit?: boolean;

    @Input()
    hint: string;

    @Output()
    onValueChanged = new EventEmitter<ValueChangedParms>();

    @Input()
    error: string;

    @Input()
    allowExtension: boolean;

    @Input()
    compact?: boolean;

    @Input()
    showSpinner: boolean;

    /** Accessible in the template */ isViewOnly: boolean;

    private isInitialized: boolean;

    /**
     * Metadata's Recalculate on change functionality mutates the metadataModel in a way that will not trigger a binding change event,
     * so we need to have a way to do a simple check in `$doCheck` to determine if the `this.model` value was changed with out our notice.
     * This should only be set in `$onInit` and `onModelChanged`.
     */
    private unboundModel: string;

    private _valueModel: string = "";

    private readonly debouncer$ = new Subject<void>();
    private readonly destroy$ = new Subject<void>();

    density: FieldDensity;

    ngOnInit(): void
    {
        const strippedModel = this.stripFormatting(this.model);
        this.unboundModel = strippedModel;
        this.valueModel = this.$metadataInputFormatterService.stringToPhone(strippedModel, this.allowExtension);

        if (this.forCellEdit)
            this.debouncer$.pipe(debounceTime(10), takeUntil(this.destroy$)).subscribe(() => this.fireValueChanged());

        this.density = this.$metadataService.getInputDensity(this.compact);
    }

    ngOnDestroy(): void
    {
        if (this.forCellEdit)
        {
            this.destroy$.next();
            this.destroy$.complete();
            this.debouncer$.complete();
        }
    }

    ngAfterViewInit(): void
    {
        if (this.isInitialized)
            return;
        this.isInitialized = true;
    }

    ngDoCheck(): void
    {
        if (!this.isInitialized)
        {
            return;
        }
        if (this.unboundModel !== this.model)
        {
            this.onModelChanged(this.model);
        }
        this.isViewOnly =
            (this.viewOnly && !this.propertyMetadata.ViewOnlyEnabled) ||
            this.$metadataPropertyService.isViewOnly(this.propertyMetadata, this.metadataModel, this.parent);
    }

    ngOnChanges(_changes: SimpleChanges): void
    {
        const changedKeys = Object.keys(_changes);
        for (const key of changedKeys)
        {
            switch (key)
            {
                case "model":
                    const change = _changes[key];
                    this.onModelChanged(change.currentValue);
                    break;
            }
        }
    }

    get Regex(): RegExp
    {
        return this.allowExtension && this.valueModel?.length > 10
            ? /\(\d{3}\) \d{3}-\d{4}(?: [x]\d10?)?/
            : /\(\d{3}\) \d{3}-\d{4}/;
    }

    get regexPatternString(): string
    {
        return this.Regex.toString();
    }

    get isInvalid(): boolean
    {
        return this.$metadataService.invalidInput(this.error, this.modelGroup?.control?.controls[this.propertyMetadata.Name]);
    }

    private onModelChanged(value: string): void
    {
        this.unboundModel = value;
        if (!value)
        {
            this.valueModel = null;
        }
        else if (!this.valueModel || this.valueModel !== value)
        {
            this.valueModel = value;
        }
    }

    get valueModel(): string
    {
        return this._valueModel;
    }

    set valueModel(value: string)
    {
        if (!Utilities.IsNullOrUndefined(value))
            this._valueModel = this.$metadataInputFormatterService.stringToPhone(value, this.allowExtension);
        else
            this._valueModel = value;

    }

    get InputType(): string
    {
        if (Utilities.IsNullOrUndefined(this.propertyMetadata)) return "";
        return this.propertyMetadata.InputType;
    }

    get Name(): string
    {
        return this.propertyMetadata.Name;
    }

    get controlId(): string
    {
        if (Utilities.IsNullOrUndefined(this.metadataModel)) return `txt_${this.Name}`;
        let id = this.metadataModel.$controlId;
        if (!Utilities.IsNullOrUndefined(id)) return `txt_${this.Name}_${id}`;
        id = this.metadataModel.$controlId = this.metadataModel.Id;
        if (Utilities.IsNullOrUndefinedOrEmpty(id) || id === EMPTY_GUID)
        {
            id = this.metadataModel.$controlId = GuidHelper.newGuid();
        }
        return `txt_${this.Name}_${id}`;
    }

    get Label(): string
    {
        return this.forCellEdit
            ? ""
            : this.$metadataService.EvaluateString(this.propertyMetadata.Label, this.parent, this.metadataModel);
    }

    get LabelPosition(): string
    {
        return this.forCellEdit ? 'none' : this.compact ? 'block-start' : 'inset';
    }

    get MinLength(): number
    {
        return this.propertyMetadata.MinLength;
    }

    get MaxLength(): number
    {
        return this.$metadataInputTypeHelperService.getMaxLength(this.propertyMetadata, this.metadataModel, this.parent);
    }

    GetToolTipText(): string
    {
        return this.propertyMetadata.TooltipText;
    }

    HasTooltip(): boolean
    {
        return !!this.propertyMetadata && !!this.propertyMetadata.Tooltip;
    }

    Rows(): number
    {
        if (this.propertyMetadata.Rows) return this.propertyMetadata.Rows;
        return 5;
    }

    get isRequired(): boolean
    {
        return (
            this.$metadataPropertyService.isRequired(this.propertyMetadata, this.metadataModel, this.parent) &&
            !this.isViewOnly
        );
    }

    onInputChange(): void
    {
        if (this.forCellEdit)
            this.debouncer$.next();
    }

    onChange(): void
    {
        if (!this.forCellEdit)
            this.fireValueChanged();
    }

    visualmodel: string;

    fireValueChanged(): void
    {
        if (this.stripFormatting(this.model) === this.stripFormatting(this.valueModel))
        {
            return;
        }
        requestAnimationFrame(() => this.onValueChanged.emit({ value: this.applyFormatting(this.valueModel) }));
    }

    stripFormatting(phone: string): string
    {
        return Utilities.IsNullOrUndefinedOrEmpty(phone) ? "" : phone.replace(/\D/g, "");
    }

    applyFormatting(model: string): string
    {
        return !Utilities.IsNullOrUndefined(model) ? this.updateFormat(this.stripFormatting(model)) : "";
    }

    updateFormat(phone: string): string
    {
        if (!this.allowExtension)
        {
            phone = phone.substring(0, 10);
        }
        const backendFormat: string = this.propertyMetadata.RegexProperty;
        return phone.replace(/(\d{3})(\d{3})(\d{4})(\d{0,10})/, backendFormat);
    }

    isNumericInput(event): boolean
    {
        const key = event.key;
        if (key === "Backspace" || key === "Tab") return true;
        const regex = new RegExp('^\\d$');
        return regex.test(key);
    }

    enforceFormat(event): void
    {
        if (event.code === "NumpadAdd" && this.$securityService.UseTenKeyPlusAsTab)
        {
            event.preventDefault();
            this.$keyHandlerService.selectNextTabbableElement();
        }
        else if (!this.isNumericInput(event) && !this.$keyHandlerService.ignoreForFormatting(event))
        {
            event.preventDefault();
        }
    }

    formatToPhone(event): void
    {
        if (!this.$keyHandlerService.ignoreForFormatting(event))
            event.target.value = this.$metadataInputFormatterService.stringToPhone(event.target.value, this.allowExtension);
    }
}
